import ApprovalStatus from "types/ApprovalStatus";
import ActionButton from "components/base/ActionButton";
import IconCheck from "components/icons/IconCheck";
import IconEmailCheck from "components/icons/IconEmailCheck";
import IconTimeSand from "components/icons/IconTimeSand";
import { postApproval, postOvertake, postRelease, putNextApprover } from "../../api";
import useRequestHeaders from "../../hooks/useRequestHeaders";
import { useContext } from "react";
import NotificationContext from "../../contexts/NotificationContext";
import IconIncrease from "../icons/IconIncrease";
import { ModalProperties } from "../../types/ModalProperties";
import { useLanguage } from "../../contexts/LanguageContext";
import Labeled from "./Labeled";

interface ProposalActionProps {
  className?: string;
  upn: string;
  name: string;
  status: ApprovalStatus;
  availableActions: string;
  showIncreaseLimitWarning: boolean;
  setModalProperties?: (modalProperties: ModalProperties) => void;
  refetchFn: () => void;
  notEditable: boolean;
  validate?: () => string;
  updateDataFn?: () => void;
  disabled?: boolean;
}

const icons = {
  [ApprovalStatus.IN_PROGRESS]: <IconEmailCheck />,
  [ApprovalStatus.READY_TO_APPROVE]: <IconTimeSand />,
  [ApprovalStatus.RELEASED]: <IconCheck />,
  [ApprovalStatus.APPROVED]: <IconEmailCheck />,
  OVERTAKE: <IconIncrease />,
};

const ProposalAction = ({
  className,
  upn,
  name,
  status,
  availableActions,
  showIncreaseLimitWarning,
  setModalProperties,
  refetchFn,
  notEditable,
  validate,
  disabled,
}: ProposalActionProps) => {
  const { messages } = useLanguage();

  const headers = useRequestHeaders();
  const setNotification = useContext(NotificationContext)[1];

  const hasAvailableActions = !["NONE"].includes(availableActions);

  const handleAction = async () => {
    if (availableActions === "APPROVE") {
      await postApproval(upn, headers)
        .then(() => {
          refetchFn();
        })
        .catch(() => setNotification(messages.approveSalaryProposal.failed));
    } else if (availableActions === "OVERTAKE") {
      await postOvertake(upn, headers)
        .then(() => {
          refetchFn();
        })
        .catch(() => setNotification(messages.approveSalaryProposal.failed));
    } else if (availableActions === "RELEASE" || availableActions === "HR_RELEASE") {
      await postRelease(upn, headers)
        .then(() => {
          refetchFn();
        })
        .catch(() => setNotification(messages.approveSalaryProposal.failed));
    } else if (availableActions === "EDIT") {
      await putNextApprover(upn, headers)
        .then(() => {
          refetchFn();
        })
    }
    if (setModalProperties) {
      setModalProperties({
        visible: false,
        header: "",
        content: "",
        showCloseButton: false,
        confirmButtonText: "",
        confirmButtonAction: undefined,
      });
    }
  };

  const onClick = async () => {
    if (setModalProperties) {
      if (validate && (availableActions === "APPROVE" || availableActions === "EDIT")) {
        const validationResult = validate();
        if (validationResult !== "") {
          setModalProperties({
            visible: true,
            header: messages.information.validationFailed,
            content: (
              <div>
                <span>{validationResult}</span>
              </div>
            ),
            showCloseButton: true,
            confirmButtonText: "OK",
            confirmButtonAction: undefined,
          });
          return;
        }
      }

      setModalProperties({
        visible: true,
        header: messages.actionHeader[availableActions],
        content: (
          <div>
            <span>{messages.actionText[availableActions]}</span>
            <br />
            <br />
            <ul>
              <li key={"employee-" + availableActions + upn}>
                {showIncreaseLimitWarning ? (
                  <Labeled
                    className={"labeled-amount"}
                    editable={false}
                    label={""}
                    suffix={""}
                    text={name + " (" + messages.showIncreaseLimitWarning.warn.header + ")"}
                    warning={true}
                  />
                ) : (
                  <Labeled
                    className={"labeled-amount"}
                    editable={false}
                    label={""}
                    text={name}
                    suffix={""}
                    warning={false}
                  />)
                }
              </li>
            </ul>
          </div>
        ),
        showCloseButton: true,
        confirmButtonText: "OK",
        confirmButtonAction: handleAction,
      });
    }
  };

  const getLabelForAction = () => {
    if (availableActions === "OVERTAKE") {
      return messages.proposalAction.OVERTAKE;
    } else if (availableActions === "APPROVE") {
      return messages.proposalAction.IN_PROGRESS;
    } else if (availableActions === "RELEASE"  || availableActions === "HR_RELEASE") {
      return messages.proposalAction.RELEASE;
    } else if (availableActions === "EDIT") {
      return messages.proposalAction.IN_PROGRESS;
    } else if (notEditable) {
      return messages.proposalAction.FORWARDED;
    } else {
      return messages.proposalAction[status];
    }
  };

  return (
    <ActionButton
      className={className}
      disabled={!hasAvailableActions || disabled}
      onClick={onClick}
      warning={availableActions === "OVERTAKE"}
      action={availableActions === "APPROVE" || availableActions === "RELEASE"  || availableActions === "HR_RELEASE" || availableActions === "EDIT"}
      label={getLabelForAction()}
    >
      {availableActions === "OVERTAKE" && status === ApprovalStatus.IN_PROGRESS
        ? icons.OVERTAKE
        : icons[status]}
    </ActionButton>
  );
};

export default ProposalAction;
